/**
 * `useMessages` — Message-history + realtime hook for a single chat room.
 *
 * Responsibilities (design.md §Frontend Layer › useMessages,
 * Requirements R4.2 / R5.2 / R5.3 / R6.1 / R6.4):
 *   - On mount: fetch the most-recent page via `chatApi.listMessages`. The
 *     server returns DESC by seq, so we reverse to keep `messages` in
 *     ascending order (古→新) — that's the natural reading direction for
 *     the UI.
 *   - Subscribe to `signalR.onNewMessage` and append realtime pushes to
 *     the tail of the list. Deduplicate by `seq` so an echoed push (the
 *     server broadcasts to the sender too) or a re-delivery doesn't
 *     create phantom duplicates.
 *   - Provide a `loadOlder()` action that uses the current oldest seq as
 *     `beforeSeq` for the next page. Respects `hasMore` so an exhausted
 *     history short-circuits to a no-op.
 *   - Tear down the SignalR subscription on unmount (no listener leaks).
 *
 * Dependency direction (design.md §File Structure Plan):
 *   - This hook depends only on `../api/chatApi` and `../api/signalRClient`
 *     types. No upstream coupling to React-only utilities (other than
 *     React itself) or UI components.
 *
 * State model:
 *   - `messages`           — `Message[]` in ascending seq order.
 *   - `hasMore`            — page-cursor; true while older pages exist.
 *   - `isLoading`          — true during the initial fetch only.
 *     (`loadOlder` doesn't flip this — callers can derive a per-action
 *      busy flag if needed; keeping the flag scoped avoids flicker on
 *      the initial-load skeleton.)
 *   - `error`              — UI-ready string when a fetch fails. We do
 *     NOT surface a discriminated union here; the design calls for a
 *     simple displayable string at this layer.
 *   - SignalR subscription lives in an effect keyed on `signalR` so the
 *     listener attaches once and detaches on unmount.
 *
 * Concurrency notes:
 *   - Both the initial fetch and `loadOlder()` mutate `messages`. They
 *     never overlap because `loadOlder()` early-returns while
 *     `isLoading` is true (initial fetch in flight) and while
 *     `loadingOlder` is true (a previous `loadOlder()` in flight). The
 *     guard is held in refs so back-to-back invocations of the same
 *     render see consistent values.
 *   - The "unmounted" guard uses a ref toggled in the cleanup effect so
 *     late-arriving fetch resolutions don't `setState` on an unmounted
 *     component.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ApiResult,
  ChatApi,
  ChatApiError,
  DisplayMessage,
  Message,
} from '../api/chatApi';
import type { SignalRClient } from '../api/signalRClient';

// ---------------------------------------------------------------------------
// Optimistic-send internals
// ---------------------------------------------------------------------------

/**
 * A message the user has sent that is not yet confirmed by the server. Held
 * separately from the confirmed `messages` array (which is keyed/ordered by
 * the server-assigned `seq`) because a pending message has no real seq until
 * the POST resolves.
 */
interface PendingEntry {
  readonly tempId: string;
  readonly content: string;
  /** Client-clock ISO timestamp, used only for display ordering/rendering. */
  readonly sentAt: string;
  readonly status: 'sending' | 'failed';
  readonly error?: ChatApiError;
}

let tempIdCounter = 0;

/** Generate a unique client-side id for an optimistic message. */
function makeTempId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid !== undefined) return `pending-${uuid}`;
  tempIdCounter += 1;
  return `pending-${Date.now()}-${tempIdCounter}`;
}

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** Options accepted by `useMessages`. */
export interface UseMessagesOptions {
  /** REST client used for history fetches. */
  readonly chatApi: ChatApi;
  /** Realtime client used for `newMessage` push subscription. */
  readonly signalR: SignalRClient;
  /** The room whose messages we're observing. */
  readonly roomId: string;
  /** The viewer's nickname — stamped on optimistic (pending) messages. */
  readonly myNickname: string;
  /** Page size for history fetches. Defaults to 50 (design.md §listMessages). */
  readonly pageSize?: number;
}

/** Result shape returned from `useMessages`. */
export interface UseMessagesResult {
  /**
   * Loaded messages in ascending order (oldest first): server-confirmed
   * messages by seq, followed by any optimistic (pending) messages awaiting
   * confirmation. Pending entries carry a `pending` marker so the UI can
   * render them as "送信中" / "失敗".
   */
  readonly messages: readonly DisplayMessage[];
  /**
   * Send a message optimistically. The message appears in `messages` as a
   * `pending` entry immediately; on success it is replaced by the
   * server-confirmed message, on failure it is flagged `failed` (retry via
   * `resend`). Resolves with the underlying API result.
   */
  readonly sendMessage: (content: string) => Promise<ApiResult<Message>>;
  /** Retry a previously failed optimistic message by its temp id. */
  readonly resend: (tempId: string) => Promise<void>;
  /** Fetch the next-older page. No-op if `hasMore` is false or a fetch
   * is already in flight. */
  readonly loadOlder: () => Promise<void>;
  /**
   * Re-fetch the latest page of messages and merge (dedup by seq) into the
   * current list. Used by the chat-view reload button to recover from
   * realtime drops without losing any older history the user has already
   * paged in. No-op if a refresh or initial load is already in flight.
   */
  readonly refresh: () => Promise<void>;
  /** Whether the server reports more older pages exist. */
  readonly hasMore: boolean;
  /** True only during the initial fetch. */
  readonly isLoading: boolean;
  /** True while a manual `refresh()` is in flight. */
  readonly isRefreshing: boolean;
  /** UI-ready failure reason from the initial fetch (null on success). */
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMessages(options: UseMessagesOptions): UseMessagesResult {
  const {
    chatApi,
    signalR,
    roomId,
    myNickname,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;

  // -- State -------------------------------------------------------------
  // `messages` is held in state for re-render triggering and also
  // mirrored to a ref so async callbacks (`loadOlder`, the SignalR
  // listener) can read the latest value without re-subscribing.
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  const [hasMore, setHasMore] = useState<boolean>(false);
  const hasMoreRef = useRef<boolean>(hasMore);
  hasMoreRef.current = hasMore;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isLoadingRef = useRef<boolean>(true);
  isLoadingRef.current = isLoading;

  const [error, setError] = useState<string | null>(null);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const isRefreshingRef = useRef<boolean>(false);
  isRefreshingRef.current = isRefreshing;

  // Guards: prevent overlapping loadOlder calls and protect against
  // setState-after-unmount races.
  const loadingOlderRef = useRef<boolean>(false);
  const unmountedRef = useRef<boolean>(false);

  // Optimistic (pending) messages: shown immediately on send, before the
  // server confirms. Mirrored to a ref so `resend` can read the latest list
  // without re-binding its callback on every pending change.
  const [pending, setPending] = useState<PendingEntry[]>([]);
  const pendingRef = useRef<PendingEntry[]>(pending);
  pendingRef.current = pending;

  // -- Initial fetch -----------------------------------------------------
  // Effect runs once per (chatApi, roomId, pageSize) tuple. In normal
  // production usage these are stable for the component's lifetime.
  useEffect(() => {
    unmountedRef.current = false;
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    (async () => {
      const result = await chatApi.listMessages(roomId, { limit: pageSize });
      if (cancelled || unmountedRef.current) return;

      if (result.ok) {
        // Server returns DESC by seq; flip to ASC so the array reads
        // oldest-first (matches what MessageList renders top→bottom).
        const ascending = [...result.value.items].reverse();
        setMessages(ascending);
        setHasMore(result.value.hasMore);
        setError(null);
      } else {
        setError(describeError(result.error));
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // chatApi, roomId, pageSize are all stable for the typical caller;
    // if any actually changes we DO want to refetch.
  }, [chatApi, roomId, pageSize]);

  // -- SignalR newMessage subscription ----------------------------------
  // Separate effect so it's not torn down on every re-fetch. The
  // listener reads `messagesRef.current` for dedup so it doesn't need
  // `messages` in the dep array.
  useEffect(() => {
    const unsubscribe = signalR.onNewMessage((incoming) => {
      if (unmountedRef.current) return;
      // Dedup by seq: the server broadcasts to the sender too (echo),
      // and SignalR Free tier is at-most-once but the realtime stream
      // can race the REST initial fetch, so a duplicate seq can arrive
      // legitimately. Keep the existing entry — it was written by the
      // canonical fetch path.
      const current = messagesRef.current;
      if (current.some((m) => m.seq === incoming.seq)) return;
      // Append to tail. Within the seq-ascending invariant, a newly
      // pushed message has seq > any existing seq (server-assigned
      // monotonic). Defensive: callers that re-emit older seqs (e.g.
      // tests) still see the message at the end; UI ordering remains
      // by seq via downstream sort if it matters. Cheap path here is
      // append-only.
      setMessages([...current, incoming]);
    });

    return () => {
      unsubscribe();
    };
  }, [signalR]);

  // -- Unmount guard -----------------------------------------------------
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // -- loadOlder action --------------------------------------------------
  const loadOlder = useCallback(async () => {
    // Skip if there's nothing older to fetch.
    if (!hasMoreRef.current) return;
    // Skip while initial fetch is in flight — `messages[0].seq` isn't
    // yet meaningful.
    if (isLoadingRef.current) return;
    // Skip overlapping calls.
    if (loadingOlderRef.current) return;
    // Defensive: also skip when we have no anchor yet.
    if (messagesRef.current.length === 0) return;

    loadingOlderRef.current = true;
    try {
      const beforeSeq = messagesRef.current[0]!.seq;
      const result = await chatApi.listMessages(roomId, {
        limit: pageSize,
        beforeSeq,
      });
      if (unmountedRef.current) return;

      if (result.ok) {
        const ascending = [...result.value.items].reverse();
        // Prepend; sort invariant preserved because every item in
        // `ascending` has seq < current head's seq (server contract:
        // `beforeSeq` is exclusive upper bound).
        setMessages([...ascending, ...messagesRef.current]);
        setHasMore(result.value.hasMore);
      } else {
        setError(describeError(result.error));
      }
    } finally {
      loadingOlderRef.current = false;
    }
  }, [chatApi, roomId, pageSize]);

  // -- refresh action ----------------------------------------------------
  // Re-fetches the latest page and merges into the existing list (dedup by
  // seq). Preserves any older pages the user has already paged in via
  // `loadOlder()`, so the scroll history isn't lost. Skipped while the
  // initial fetch or another refresh is in flight.
  const refresh = useCallback(async () => {
    if (isLoadingRef.current) return;
    if (isRefreshingRef.current) return;

    setIsRefreshing(true);
    try {
      const result = await chatApi.listMessages(roomId, { limit: pageSize });
      if (unmountedRef.current) return;

      if (result.ok) {
        const incoming = [...result.value.items].reverse();
        // Merge: dedup by seq, preserve existing entries for seqs we already
        // have (their content is canonical from the original fetch path).
        const seen = new Set<number>();
        const merged: Message[] = [];
        for (const m of messagesRef.current) {
          if (seen.has(m.seq)) continue;
          seen.add(m.seq);
          merged.push(m);
        }
        for (const m of incoming) {
          if (seen.has(m.seq)) continue;
          seen.add(m.seq);
          merged.push(m);
        }
        merged.sort((a, b) => a.seq - b.seq);
        setMessages(merged);
        // `hasMore` for the latest page tells us whether older pages exist
        // *between* the latest page and the previous tail. If the user has
        // already paged older messages, the previous `hasMore` from older
        // pages is the authoritative cursor — only adopt the new value
        // when the existing tail is empty or starts at this page's range.
        if (messagesRef.current.length === 0) {
          setHasMore(result.value.hasMore);
        }
        setError(null);
      } else {
        setError(describeError(result.error));
      }
    } finally {
      if (!unmountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [chatApi, roomId, pageSize]);

  // -- Optimistic send ---------------------------------------------------
  // Apply the outcome of a send/resend attempt to local state. On success the
  // pending entry is dropped and the confirmed message merged into the list
  // (deduped by seq — the SignalR echo may have already appended it). On
  // failure the entry is flagged `failed` so the UI can offer a retry.
  const reconcile = useCallback(
    (tempId: string, result: ApiResult<Message>) => {
      if (unmountedRef.current) return;
      if (result.ok) {
        const saved = result.value;
        setPending((prev) => prev.filter((p) => p.tempId !== tempId));
        setMessages((prev) =>
          prev.some((m) => m.seq === saved.seq) ? prev : [...prev, saved],
        );
      } else {
        setPending((prev) =>
          prev.map((p) =>
            p.tempId === tempId
              ? { ...p, status: 'failed', error: result.error }
              : p,
          ),
        );
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (content: string): Promise<ApiResult<Message>> => {
      const tempId = makeTempId();
      const sentAt = new Date().toISOString();
      setPending((prev) => [
        ...prev,
        { tempId, content, sentAt, status: 'sending' },
      ]);

      const result = await chatApi.sendMessage(roomId, {
        nickname: myNickname,
        content,
      });
      reconcile(tempId, result);
      return result;
    },
    [chatApi, roomId, myNickname, reconcile],
  );

  const resend = useCallback(
    async (tempId: string): Promise<void> => {
      const entry = pendingRef.current.find((p) => p.tempId === tempId);
      if (entry === undefined) return;

      // Flip back to `sending` (clears the prior error) before retrying.
      setPending((prev) =>
        prev.map((p) =>
          p.tempId === tempId
            ? { ...p, status: 'sending', error: undefined }
            : p,
        ),
      );

      const result = await chatApi.sendMessage(roomId, {
        nickname: myNickname,
        content: entry.content,
      });
      reconcile(tempId, result);
    },
    [chatApi, roomId, myNickname, reconcile],
  );

  // -- Merge confirmed + pending for display -----------------------------
  // Pending messages render after the newest confirmed message. We synthesise
  // monotonically increasing sentinel seqs (above the latest confirmed seq)
  // so MessageList's "stick to bottom on new message" logic still fires when
  // a fresh optimistic message is appended.
  const displayMessages = useMemo<DisplayMessage[]>(() => {
    if (pending.length === 0) return messages;
    const baseSeq =
      messages.length === 0 ? 0 : messages[messages.length - 1]!.seq;
    const pendingDisplay: DisplayMessage[] = pending.map((p, i) => ({
      id: p.tempId,
      roomId,
      seq: baseSeq + 1 + i,
      senderNickname: myNickname,
      content: p.content,
      sentAt: p.sentAt,
      pending: { tempId: p.tempId, status: p.status, error: p.error },
    }));
    return [...messages, ...pendingDisplay];
  }, [messages, pending, roomId, myNickname]);

  return {
    messages: displayMessages,
    sendMessage,
    resend,
    loadOlder,
    refresh,
    hasMore,
    isLoading,
    isRefreshing,
    error,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map a `ChatApiError` into a short UI-ready string. The hook surfaces
 * a single string (not a discriminated union) by design — components
 * just render it as-is, so any switch on `kind` lives here.
 */
function describeError(err: ChatApiError): string {
  switch (err.kind) {
    case 'invalidInput':
      return '入力内容に誤りがあります';
    case 'notJoined':
      return 'このルームに参加していません';
    case 'roomNotFound':
      return 'ルームが見つかりません';
    case 'roomFull':
      return 'ルームが満員です';
    case 'guestLimitReached':
      return `メッセージの送信上限に達しました（連続 ${err.limit} 件まで）`;
    case 'bannedWord':
      return err.word.length > 0
        ? `NGワード「${err.word}」が含まれています`
        : 'NGワードが含まれています';
    case 'rateLimited':
      return '操作が多すぎます。しばらく待ってから再試行してください';
    case 'concurrencyConflict':
      return '更新が競合しました。再試行してください';
    case 'serverError':
      return 'サーバエラーが発生しました';
    case 'networkError':
      return 'ネットワークエラーが発生しました';
    default: {
      // Exhaustiveness check.
      const _exhaustive: never = err;
      void _exhaustive;
      return '不明なエラーが発生しました';
    }
  }
}
